import React, { useState, useEffect, useMemo } from 'react';
import { auth } from '../firebase';
import { getArtistVerification, submitArtistVerification } from '../utils/secureFunctions';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import type { ModerationTokenMap } from '../utils/moderatedUpload';
import TopNavigation from '../components/TopNavigation';
import { useNavigate } from 'react-router-dom';

type VType = 'creator' | 'artist' | 'public_figure';

const TYPE_INFO: Record<VType, { label: string; help: string; docs: { type: string; label: string; required?: boolean }[] }> = {
  creator: {
    label: 'Content Creator',
    help: 'Verify your identity as a content creator.',
    docs: [
      { type: 'government_id', label: "Government-issued ID (passport, national ID, driver's licence)", required: true },
      { type: 'proof_of_ownership', label: 'Proof of content ownership' },
      { type: 'tax_form', label: 'Tax form' },
      { type: 'bank_statement', label: 'Bank statement' },
      { type: 'utility_bill', label: 'Utility bill / proof of address' },
      { type: 'other', label: 'Other supporting document' },
    ],
  },
  artist: {
    label: 'Music Artist',
    help: 'Upload your ID and proof that you own the rights to your music.',
    docs: [
      { type: 'government_id', label: "Government-issued ID (passport, national ID, driver's licence)", required: true },
      { type: 'proof_of_ownership', label: 'Proof of music ownership (distribution/label contract, copyright or PRO registration)', required: true },
      { type: 'bank_statement', label: 'Bank statement' },
      { type: 'other', label: 'Other supporting document (e.g. streaming-platform artist profile)' },
    ],
  },
  public_figure: {
    label: 'Public Figure',
    help: 'Ministers, artists, authors and other notable people can request the blue verified tick.',
    docs: [
      { type: 'government_id', label: "Government-issued ID (passport, national ID, driver's licence)", required: true },
      { type: 'proof_of_public_profile', label: 'Proof of public profile (news articles, official website, verified profiles elsewhere)', required: true },
      { type: 'other', label: 'Other supporting document' },
    ],
  },
};

const ERROR_TEXT: Record<string, string> = {
  NOT_ENOUGH_FOLLOWERS: 'This verification type is not available for your account yet.',
  MISSING_DOCUMENTS: 'Please upload all required documents.',
  REQUEST_PENDING: 'You already have a verification request under review.',
  ALREADY_VERIFIED: 'You are already verified for this type.',
  INVALID_DOCUMENT: 'One of the documents is invalid. Please upload it again.',
};

const statusText = (s: string) => (s === 'additional_info_required' ? 'MORE INFORMATION NEEDED' : String(s || '').toUpperCase());

const ArtistVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [verificationType, setVerificationType] = useState<VType>('creator');
  const [documents, setDocuments] = useState<{ type: string; file: File }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [existingVerification, setExistingVerification] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [profileVerified, setProfileVerified] = useState(false);
  const [publicFigureEligible, setPublicFigureEligible] = useState(false);

  useEffect(() => {
    if (!auth.currentUser) return;
    getArtistVerification()
      .then((res) => {
        if (res.verification) setExistingVerification(res.verification);
        setProfileVerified(!!res.profileVerified);
        setPublicFigureEligible(!!res.eligible?.public_figure);
      })
      .catch((error) => console.error('Failed to check verification status:', error));
  }, []);

  const info = TYPE_INFO[verificationType];
  const isQualified = verificationType !== 'public_figure' || publicFigureEligible;
  const missingRequired = useMemo(
    () => info.docs.filter((d) => d.required && !documents.some((x) => x.type === d.type)),
    [info, documents],
  );

  const changeType = (t: VType) => {
    setVerificationType(t);
    setDocuments([]);
  };

  const handleFileAdd = (type: string, file: File) => {
    setDocuments(prev => [...prev, { type, file }]);
  };

  const handleRemoveFile = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!auth.currentUser) {
      alert('Please log in');
      return;
    }

    if (missingRequired.length) {
      alert(`Please upload: ${missingRequired.map((d) => d.label).join(', ')}`);
      return;
    }

    setUploading(true);

    try {
      // Upload documents to private R2 via VPS-signed URLs
      const uploadedDocs = await Promise.all(
        documents.map(async ({ type, file }, index) => {
          const upload = await uploadModeratedMedia({
            folder: 'creator-verification',
            field: `documents.${type}.${index}` ,
            file,
            contentType: file.type || 'application/octet-stream',
          });

          return {
            type,
            url: upload.objectKey,
            fileName: file.name,
            uploadedAt: new Date(),
            storagePath: upload.storagePath,
            moderationProof: upload.proof,
            field: upload.field
          };
        })
      );

      const moderationTokens = uploadedDocs.reduce<ModerationTokenMap>((acc, doc) => {
        if (doc.field && doc.moderationProof) {
          acc[doc.field] = doc.moderationProof;
        }
        return acc;
      }, {});

      const res = await submitArtistVerification({
        fullName,
        verificationType,
        documents: uploadedDocs,
        bankDetails: null,
        moderationTokens,
      });

      alert('Verification submitted! We will review your documents within 3-5 business days and notify you.');
      setExistingVerification(res.verification);
      setShowForm(false);
    } catch (error: any) {
      console.error('Verification submission failed:', error);
      alert(ERROR_TEXT[String(error?.code || '')] || 'Failed to submit verification. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (existingVerification && !showForm) {
    const st = existingVerification.status;
    const typeLabel = TYPE_INFO[existingVerification.verificationType as VType]?.label || existingVerification.verificationType;
    return (
      <div style={styles.container}>
        <TopNavigation />
        <div style={styles.content}>
          <h1 style={styles.title}>Verification Status</h1>
          <div style={styles.statusCard}>
            <p><strong>Status:</strong> {statusText(st)}</p>
            <p><strong>Name:</strong> {existingVerification.fullName || existingVerification.artistName}</p>
            <p><strong>Type:</strong> {typeLabel}</p>
            {st === 'pending' && (
              <p style={{ color: '#FF9800' }}>Your verification is under review. We'll notify you once it's processed.</p>
            )}
            {st === 'approved' && (
              <p style={{ color: '#4CAF50' }}>
                {existingVerification.verificationType === 'public_figure'
                  ? 'You are verified. Your profile shows the blue tick.'
                  : 'Your verification has been approved.'}
              </p>
            )}
            {(st === 'rejected' || st === 'additional_info_required') && (
              <>
                <p style={{ color: '#f44336' }}>{st === 'rejected' ? 'Verification rejected' : 'We need more information'}</p>
                {existingVerification.rejectionReason && <p><strong>Reason:</strong> {existingVerification.rejectionReason}</p>}
              </>
            )}
            {st !== 'pending' && (
              <button style={styles.button} onClick={() => { setShowForm(true); setDocuments([]); }}>
                {st === 'approved' ? 'Apply for another verification type' : 'Resubmit Verification'}
              </button>
            )}
            <button style={{ ...styles.button, marginLeft: 8, background: '#5B6E6E' }} onClick={() => navigate('/earnings')}>
              Go to Earnings
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TopNavigation />
      <div style={styles.content}>
        <h1 style={styles.title}>Verification</h1>
        <p style={styles.subtitle}>{info.help}</p>

        {profileVerified && (
          <div style={{ ...styles.warningBanner, background: '#E8F5E9', border: '1px solid #A5D6A7' }}>
            <p>Your profile already has the blue verified tick.</p>
          </div>
        )}

        {!isQualified && (
          <div style={styles.warningBanner}>
            <p>Public figure verification is not available for your account yet.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Verification Type</label>
            <select
              value={verificationType}
              onChange={(e) => changeType(e.target.value as VType)}
              style={styles.input}
            >
              {(Object.keys(TYPE_INFO) as VType[]).map((t) => (
                <option key={t} value={t}>{TYPE_INFO[t].label}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Full Legal Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              style={styles.input}
              placeholder="Your full legal name as it appears on ID"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Upload Documents</label>
            <p style={styles.helpText}>Documents marked * are required. Files are stored privately and only seen by our review team.</p>

            <div style={styles.fileUploadSection}>
              {info.docs.map(doc => (
                <div key={doc.type} style={styles.fileRow}>
                  <label style={styles.fileLabel}>
                    {doc.label}{doc.required ? ' *' : ''}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleFileAdd(doc.type, e.target.files[0]);
                        }
                        e.target.value = '';
                      }}
                      style={styles.fileInput}
                    />
                  </label>
                </div>
              ))}
            </div>

            <div style={styles.uploadedFiles}>
              {documents.map((doc, idx) => (
                <div key={idx} style={styles.fileChip}>
                  {doc.type.replace(/_/g, ' ')}: {doc.file.name}
                  <button type="button" onClick={() => handleRemoveFile(idx)} style={styles.removeBtn}>×</button>
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={uploading || missingRequired.length > 0 || !isQualified} style={{ ...styles.submitBtn, opacity: uploading || missingRequired.length > 0 || !isQualified ? 0.6 : 1 }}>
            {uploading ? 'Uploading...' : isQualified ? 'Submit Verification' : 'Not available yet'}
          </button>
          {existingVerification && (
            <button type="button" style={{ ...styles.button, background: '#5B6E6E' }} onClick={() => setShowForm(false)}>Cancel</button>
          )}
        </form>
      </div>
    </div>
  );
};
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5EDE1',
  },
  content: {
    paddingTop: 80,
    maxWidth: 800,
    margin: '0 auto',
    padding: '20px',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#072B2C',
  },
  subtitle: {
    fontSize: 16,
    color: '#5B6E6E',
    marginBottom: 32,
  },
  warningBanner: {
    background: '#FFF3CD',
    border: '1px solid #FFE082',
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  form: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: '500',
    color: '#072B2C',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  helpText: {
    fontSize: 12,
    color: '#777',
    marginTop: 4,
  },
  fileUploadSection: {
    marginTop: 12,
  },
  fileRow: {
    marginBottom: 12,
  },
  fileLabel: {
    display: 'block',
    padding: 12,
    background: '#f5f5f5',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
  },
  fileInput: {
    display: 'block',
    marginTop: 8,
  },
  uploadedFiles: {
    marginTop: 16,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  fileChip: {
    background: '#E0F7FA',
    padding: '8px 12px',
    borderRadius: 20,
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  removeBtn: {
    background: '#f44336',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: 20,
    height: 20,
    cursor: 'pointer',
    fontSize: 14,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#072B2C',
    marginTop: 32,
    marginBottom: 16,
  },
  submitBtn: {
    width: '100%',
    padding: 16,
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: 24,
  },
  statusCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  button: {
    padding: '12px 24px',
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    marginTop: 16,
  },
};

export default ArtistVerificationPage;


